<template>
	<Modal
		:id="id"
		:is-open="isModalOpen(id)"
		hide-close
		@on-cancel="closeModal"
	>
		<div class="-my-4">
			<FormTextInput
				id="global-search"
				v-model="search"
				placeholder="Search All"
				shadow-box
				show-icon
			>
				<template #icon>
					<MagnifyingGlassIcon
						class="w-5 h-5 text-mx-gray-400 dark:text-mx-gray-300"
						aria-hidden="true"
					/>
				</template>
			</FormTextInput>
      <div class="flex ml-2 space-x-4 items-center">
        <FormSwitch
            id="opt-in"
            v-model="includeNotes"
            :debounce-time="500"
        />
        <span class="dark:text-white">
				  Include Notes
			  </span>
      </div>
			<div class="h-96 overflow-y-scroll">
				<LoadingSpinner
					v-if="loading"
					class="w-24 h-24 mx-auto text-mx-gray-300 dark:text-mx-gray-500"
				/>
				<div v-else>
					<div v-if="hasResults && search && search.length > 2">
						<div
							v-for="key in Object.keys(mappedResults)"
							:key="key"
						>
							<div class="dark:text-mx-gray-300 capitalize">
								<h5 class="p-4 ">
									{{ formatResultKey(key) }}
								</h5>
								<div class="flex flex-col space-y-1">
									<NuxtLink
										v-for="result in mappedResults[key]"
										:key="result.id"
										class="p-4 rounded-lg  bg-mx-gray-100 dark:bg-mx-green-800 dark:text-mx-gray-400 text-mx-gray-500 hover:bg-[#2299C8]/80 dark:hover:bg-[#306370]/80 hover:cursor-pointer"
										@click="handleClick(result, true)"
									>
										<span>{{ parseTextStringForResultDisplay(result[key], key).str1 }}</span>
										<span class="text-black dark:text-white">{{ parseTextStringForResultDisplay(result[key], key).str2 }}</span>
										<span class="pr-2">{{ parseTextStringForResultDisplay(result[key], key).str3 }}</span>
										<span>{{ `- ${key === 'name' ? formatPhone(result.phone) : result.name}` }}</span>
									</NuxtLink>
								</div>
							</div>
						</div>
					</div>
					<div
						v-else-if="search && search.length > 2"
						class="dark:text-mx-gray-400 w-full text-center py-12"
					>
						No results for "{{ search }}"
					</div>
					<div
						v-else-if="hasRecentSearches"
						class="flex flex-col space-y-1 capitalize"
					>
						<h5 class="p-4 dark:text-mx-gray-300">
							Recent
						</h5>
						<div
							v-for="key in Object.keys(mappedRecentSearches)"
							:key="key"
							class="flex flex-col space-y-1"
						>
							<NuxtLink
								v-for="result in mappedRecentSearches[key]"
								:key="result.id"
								class="flex justify-between rounded-lg  bg-mx-gray-100 dark:bg-mx-green-800 dark:text-mx-gray-400 text-mx-gray-500 "
							>
								<div class="flex items-center grow px-4 hover:bg-[#2299C8]/80 dark:hover:bg-[#306370]/80 hover:cursor-pointer rounded-lg">
									<span
										class="flex items-center pr-2"
										@click="handleClick(result, false)"
									>{{ result[key] }}</span>
									<span>{{ `- ${key === 'name' ? formatPhone(result.phone) : result.name}` }}</span>
								</div>
								<span
									class="p-4 hover:bg-[#2299C8]/80 dark:hover:bg-[#306370]/80 hover:cursor-pointer rounded-lg"
									@click="removeFromStorage(result.id)"
								>&#x2715;</span>
							</NuxtLink>
						</div>
					</div>
					<div
						v-else
						class="dark:text-mx-gray-400 w-full text-center py-12"
					>
						No Recent Searches
					</div>
				</div>
			</div>
		</div>
	</Modal>
</template>

<script setup lang="ts">
import { MagnifyingGlassIcon } from '@heroicons/vue/20/solid'
import { useModal } from '@/composables/useModal'
import { debouncedWatch } from '@vueuse/core'
import { formatPhone } from '@/utils/formatPhone'

defineProps({
	id: {
		type: String,
		required: true
	}
})

const {
	closeModal, isModalOpen, activeModalId
} = useModal()

const search = ref('')
const mappedResults = ref()
const mappedRecentSearches = ref()
const loading = ref(false)
const includeNotes = ref(false)

debouncedWatch(search, value => {
	if (value.length < 3) return
	fetchData()
}, { debounce: 200 })

interface SearchResult {
	id: string
	address: string
	matchedOn: string[]
	name: string
	phone: string
}
interface ResultsMap {
	[key: string]: SearchResult[]
}

const formatResultKey = (key: string) => {
  switch(key) {
    case 'deviceSns':
        return 'Device SNS'
    case 'noteBody':
      return 'Notes'
    case 'macAddressesSearch':
      return 'MAC Address'
    default:
      return key
  }
}

const mapResults = (results: SearchResult[]) => {
	const resultsObj: ResultsMap = {}
	results.forEach(result => {
		if (!result.matchedOn?.length) return
		if (Object.keys(resultsObj).includes(result.matchedOn?.[0])) {
			resultsObj[result.matchedOn?.[0]].push(result)
		} else {
			Object.assign(resultsObj, { [result.matchedOn?.[0]]: [ result ] })
		}
	})
	return resultsObj
}

const hasResults = computed(() => {
	return mappedResults.value && Object.keys(mappedResults.value).length
})

const hasRecentSearches = computed(() => {
	return mappedRecentSearches.value && Object.keys(mappedRecentSearches.value).length
})

const parseTextStringForResultDisplay = (text: string, key: string) => {
  let formattedText
  if(key === 'phone') {
    formattedText = formatPhone(text)
  } else if (key === 'deviceSns') {
    const deviceArray = JSON.parse(text)
    formattedText = deviceArray?.[0]?.serial_number || ''
  } else {
    formattedText = text
  }
	let index = formattedText.toLowerCase().indexOf(search.value)
	let length = search.value.length
	if (key === 'phone') {
		index = text.indexOf(search.value)
		index++
		if (text[0] === '1') {
			index--
		}
		if (index > 3) {
			index = index + 2
		}
		if (index > 8) {
			index++
		}
		if (formattedText.substring(index, index + length).includes('-')) {
			length++
		}
		if (formattedText.substring(index, index + length).includes(')')) {
			length = length + 2
		}
	}

	return {
		str1: formattedText.substring(0, index),
		str2: formattedText.substring(index, index + length),
		str3: formattedText.substring(index + length, formattedText.length)
	}
}

const handleClick = (result: SearchResult, setToStorage: boolean) => {
	if (setToStorage) {
		sessionStorage.setItem('recentSearches', JSON.stringify([ ...JSON.parse(sessionStorage.getItem('recentSearches') || '[]'), result ]))
	}
	navigateTo(`/accounts/${result.id}`)
	search.value = ''
	closeModal()
}

const removeFromStorage = (itemId: string) => {
	sessionStorage.setItem('recentSearches', JSON.stringify(JSON.parse(sessionStorage.getItem('recentSearches') || '[]').filter(i => i.id !== itemId)))
	mappedRecentSearches.value = mapResults(JSON.parse(sessionStorage.getItem('recentSearches') || '[]'))
}

watch(activeModalId, id => {
	if (id) {
		mappedRecentSearches.value = mapResults(JSON.parse(sessionStorage.getItem('recentSearches') || '[]'))
	}
})

const fetchData = async () => {
	loading.value = true
	await $fetch(`/api/portal/search?term=${search.value}&include_notes=${includeNotes.value}`, {
		method: 'GET',
		credentials: 'include',
		onResponse({ response }) {
			mappedResults.value = mapResults(response?._data?.mappedAccounts)
		}
	}).catch(e => {
		console.error(e)
	}).finally(() => {
		loading.value = false
	})
}

watch(includeNotes, () => {
  fetchData()
})
</script>
